<template>
  <div
    ref="refMe"
    :class="{
      'culture-bonus': hasCultureBonus
    }"
    :style="myStyle"
    class="building pending-building opacity-75 pointer-events-none z-40 p-1 absolute"
  >
    <div class="cont w-full h-full flex flex-row items-center justify-center bg-black pointer-events-none">
      <PlacedBuildingImage
        :template-id="buildingTemplate.id"
        :happiness-level="happinessLevel"
        :has-pending="true"
        :rotated="rotate"
      />
      <div
        ref="refCultureBonusMarker"
        :class="{
          'hidden': !hasCultureBonus
        }"
        class="culture-bonus-marker"
      />
    </div>
  </div>
</template>

<script>
import Grid from '@/roc/grid/Grid';
import PlacedBuildingImage from '@/components/Grid/PlacedBuilding/BuildingImageDisplay.vue';
import BuildingLevel from '@/roc/building/BuildingLevel';
import {computed, nextTick, onBeforeUnmount, onMounted, ref, watch} from 'vue';
import {useApplicationStore} from '@/roc/stores/ApplicationStore';
import Rect from '@/roc/grid/Rect';
import {useLayoutStore} from '@/roc/stores/LayoutStore';

export default {
  components: {
    PlacedBuildingImage
  },
  props: {
    buildingTemplate: {
      type: Object,
      required: true
    },
    rotate: {
      type: Boolean,
      required: true
    },
    layoutData: {
      type: Object,
      required: true
    }
  },
  emits: ['place'],
  setup (props, context) {
    const appStore = useApplicationStore();
    const lytStore = useLayoutStore();
    
    const buildingWidth = ref(props.layoutData.rotate ? props.buildingTemplate.height : props.buildingTemplate.width);
    const buildingHeight = ref(props.layoutData.rotate ? props.buildingTemplate.width : props.buildingTemplate.height);
    const happinessLevel = ref(0);
    const happiness = ref(null);
    
    /** @type {Ref<HTMLDivElement>} */
    const refMe = ref(null);
    /** @type {Ref<HTMLDivElement>} */
    const refCultureBonusMarker = ref(null);

    const hasCultureBonus = computed(() => {
      return props.buildingTemplate.effects.providesCulture();
    });

    const canBeHappy = computed(() => {
      return props.buildingTemplate.effects.getHappiness() !== null;
    });

    const calculateHappiness = () => {
      const cbBuildings = document.getElementsByClassName('culture-bonus');

      const myRect = new Rect(
        parseInt(refMe.value.style.left.replace('px', '')),
        parseInt(refMe.value.style.top.replace('px', '')),
        refMe.value.clientWidth - 1,
        refMe.value.clientHeight - 1
      );

      let _happiness = 0;

      for (let i = cbBuildings.length - 1; i >= 0; i--) {
        /** @type {HTMLDivElement} */
        const cbBuilding = cbBuildings[i];
        const range = parseInt(cbBuilding.dataset.buildingCultureBonusRange);

        const bonusRect = new Rect(
          parseInt(cbBuilding.style.left.replace('px', '')) - range * Grid.CELL_SIZE,
          parseInt(cbBuilding.style.top.replace('px', '')) - range * Grid.CELL_SIZE,
          cbBuilding.clientWidth - 1 + 2 * range * Grid.CELL_SIZE,
          cbBuilding.clientHeight - 1 + 2 * range * Grid.CELL_SIZE
        );

        const marker = document.querySelector('[data-marked-building="' + cbBuilding.dataset.buildingReference + '"]');

        if (myRect.intersects(bonusRect)) {
          cbBuilding.classList.add('emphasize');
          marker.classList.remove('hidden');
          _happiness += parseInt(cbBuilding.dataset.buildingCultureBonus);
        } else {
          cbBuilding.classList.remove('emphasize');
          marker.classList.add('hidden');
        }
      }

      if (!Number.isNaN(_happiness)) {
        happiness.value = _happiness;
      }

      const myEra = BuildingLevel.calculateBuildingEraFromLevel(props.layoutData.level, props.buildingTemplate, appStore.selectedEra);
      let _happinessLevel = 0;

      if (happiness.value !== null && happiness.value > 0) {
        const happinessEff = props.buildingTemplate.effects.getHappiness();
        if (happinessEff) {
          for (let i = 2; i >= 0; i--) {
            const value = happinessEff.getRequired(myEra, i);
            if (value > 0 && happiness.value >= value) {
              _happinessLevel = i + 1;
              break;
            }
          }
        }
      }

      happinessLevel.value = _happinessLevel;
    };

    const updateCultureBonusMarker = () => {
      const range = parseInt(refMe.value.dataset.buildingCultureBonusRange);

      const bonusW = refMe.value.clientWidth + range * Grid.CELL_SIZE * 2;
      const bonusH = refMe.value.clientHeight + range * Grid.CELL_SIZE * 2;

      refCultureBonusMarker.value.style.width = (bonusW / refMe.value.clientWidth * 100) + '%';
      refCultureBonusMarker.value.style.height = (bonusH / refMe.value.clientHeight * 100) + '%';
    };

    const applyPositionConstraints = function (x, y) {
      const oldX = parseInt(refMe.value.style.left.replace('px', ''));
      const oldY = parseInt(refMe.value.style.top.replace('px', ''));

      if (x < 0) {
        x = 0;
      } else if (x + refMe.value.clientWidth > Grid.CELL_COUNT_X * Grid.CELL_SIZE) {
        x = Grid.CELL_COUNT_X * Grid.CELL_SIZE - refMe.value.clientWidth + 1;
      }

      if (y < 0) {
        y = 0;
      } else if (y + refMe.value.clientHeight > Grid.CELL_COUNT_Y * Grid.CELL_SIZE) {
        y = Grid.CELL_COUNT_Y * Grid.CELL_SIZE - refMe.value.clientHeight + 1;
      }

      if (oldX !== x || oldY !== y) {
        refMe.value.style.left = x + 'px';
        refMe.value.style.top = y + 'px';
        return true;
      }

      return false;
    };

    const updateIntersectionInfo = function () {
      const bRect = new Rect(
        parseInt(refMe.value.style.left.replace('px', '')),
        parseInt(refMe.value.style.top.replace('px', '')),
        refMe.value.clientWidth - 1,
        refMe.value.clientHeight - 1
      );

      const obstacles = refMe.value.parentNode.querySelectorAll('.building, .expansion');

      refMe.value.classList.remove('invalid');

      for (let i = 0; i < obstacles.length; i++) {
        /** @type {HTMLDivElement} */
        const obstacle = obstacles[i];

        if (obstacle === refMe.value) {
          continue;
        }

        const mRect = new Rect(
          parseInt(obstacle.style.left.replace('px', '')),
          parseInt(obstacle.style.top.replace('px', '')),
          obstacle.clientWidth - 1,
          obstacle.clientHeight - 1
        );
        if (bRect.intersects(mRect)) {
          refMe.value.classList.add('invalid');
          break;
        }
      }
    };

    const onGridMouseMove = function (ev) {
      if (!refMe.value) {
        return;
      }

      let x = ev.layerX - Math.round(refMe.value.clientWidth / 2);
      let y = ev.layerY - Math.round(refMe.value.clientHeight / 2);

      if (x % Grid.CELL_SIZE !== 0) {
        if (x % Grid.CELL_SIZE >= 13) {
          x = (Math.floor(x / Grid.CELL_SIZE) + 1) * Grid.CELL_SIZE;
        } else if (x % Grid.CELL_SIZE < 13) {
          x = Math.floor(x / Grid.CELL_SIZE) * Grid.CELL_SIZE;
        }
      }

      if (y % Grid.CELL_SIZE !== 0) {
        if (y % Grid.CELL_SIZE >= 13) {
          y = (Math.floor(y / Grid.CELL_SIZE) + 1) * Grid.CELL_SIZE;
        } else if (y % Grid.CELL_SIZE < 13) {
          y = Math.floor(y / Grid.CELL_SIZE) * Grid.CELL_SIZE;
        }
      }

      if (!applyPositionConstraints(x, y)) {
        return;
      }

      updateIntersectionInfo();

      if (canBeHappy.value) {
        calculateHappiness();
      } else if (hasCultureBonus.value) {
        lytStore.updateHappiness();
      }
    };

    const onGridClick = function () {
      if (refMe.value.classList.contains('invalid')) {
        refMe.value.style.animation = 'nudge 150ms linear';

        setTimeout(() => {
          refMe.value.style.animation = 'none';
        }, 150);

        return;
      }

      context.emit('place', refMe.value);
    };

    watch(() => props.rotate, (val) => {
      if (val) {
        buildingWidth.value = props.buildingTemplate.height;
        buildingHeight.value = props.buildingTemplate.width;
      } else {
        buildingWidth.value = props.buildingTemplate.width;
        buildingHeight.value = props.buildingTemplate.height;
      }

      nextTick(() => {
        const x = parseInt(refMe.value.style.left.replace('px', ''));
        const y = parseInt(refMe.value.style.top.replace('px', ''));

        applyPositionConstraints(x, y);
        updateIntersectionInfo();
      });

      if (hasCultureBonus.value) {
        setTimeout(updateCultureBonusMarker, 5);
      }
    });

    onBeforeUnmount(() => {
      refMe.value.parentNode.removeEventListener('click', onGridClick);
      refMe.value.parentNode.removeEventListener('mousemove', onGridMouseMove);

      const cbBuildings = document.getElementsByClassName('culture-bonus');
      for (let i = cbBuildings.length - 1; i >= 0; i--) {
        /** @type {HTMLDivElement} */
        const cbBuilding = cbBuildings[i];
        const marker = document.querySelector('[data-marked-building="' + cbBuilding.dataset.buildingReference + '"]');
        if (marker) {
          marker.classList.add('hidden');
        }
        cbBuilding.classList.remove('emphasize');
      }
    });

    onMounted(() => {
      refMe.value.dataset.buildingId = props.buildingTemplate.id;
      refMe.value.dataset.buildingLevel = props.layoutData.level.toString();

      refMe.value.parentNode.addEventListener('click', onGridClick);
      refMe.value.parentNode.addEventListener('mousemove', onGridMouseMove);

      if (hasCultureBonus.value) {
        const bonus = props.buildingTemplate.effects.getCulture(props.layoutData.level, props.layoutData.era);

        refMe.value.dataset.buildingCultureBonus = bonus.value.toString();
        refMe.value.dataset.buildingCultureBonusRange = bonus.range.toString();

        if (bonus.value === 0) {
          refCultureBonusMarker.value.classList.add('hidden');
        } else {
          refCultureBonusMarker.value.classList.remove('hidden');
          setTimeout(updateCultureBonusMarker, 100);
        }

      } else if (canBeHappy.value) {
        let myEra = BuildingLevel.calculateBuildingEraFromLevel(props.layoutData.level, props.buildingTemplate, appStore.selectedEra);
        const happinessEff = props.buildingTemplate.effects.getHappiness();
        if (happinessEff) {
          for (let i = 2; i >= 0; i--) {
            if (happinessEff.getRequired(myEra, i) === 0) {
              happiness.value = 0;
              return;
            }
          }
        }
      }
    });

    return {
      happinessLevel,
      hasCultureBonus,
      refMe,
      refCultureBonusMarker,

      myStyle: computed(() => {
        return [
          'left:' + props.layoutData.x + 'px',
          'top:' + props.layoutData.y + 'px',
          'width:' + (buildingWidth.value * Grid.CELL_SIZE + 1) + 'px',
          'height:' + (buildingHeight.value * Grid.CELL_SIZE + 1) + 'px'
        ].join(';');
      }),

      displayMode: computed(() => {
        return appStore.displayMode;
      })
    };
  }
};
</script>

<style scoped>
.building {
  transition: padding, background-color 50ms ease-in;
  background-color: lightgreen;
  padding: 3px;
}

.building.invalid {
  background-color: lightcoral !important;
}

.pending-building {
  z-index: 102;
}

.culture-bonus-marker {
  position: absolute;
  border: red 2px dashed;
  pointer-events: none;
}
</style>
